import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import { api,catalogApi,type AxiosError,type MaybeEnveloped,type ListEnvelope } from '@/lib/api-client'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery,useMutation,useQueryClient,type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTheme } from 'next-themes'
import { formatDistanceToNow } from 'date-fns'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Card,CardContent,CardDescription,CardHeader,CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
X,
Plus,
MoreHorizontal,
Pencil,
Trash2,
Loader2,
Clock,
Search,
Sun,
Moon,
Blend,
Leaf,
RefreshCw,
MoreVertical,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RadioGroup,RadioGroupItem } from '@/components/ui/radio-group'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { ImageUpload } from '@/components/ui/image-upload'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { Tooltip,TooltipContent,TooltipProvider,TooltipTrigger } from '@/components/ui/tooltip'
import { StrainType } from '@/types'
import { EffectChip } from '@/features/catalog/components/effect-chip'
import { queryKeys } from '@/lib/query-keys'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { extractArray } from '@/features/http'
import { useSelectedShopId } from '@/features/shared'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import { FormDialog } from '@/components/form-dialog'
import { EffectsPicker, TerpenesPicker } from '@/features/catalog/components/ecom-vocab-pickers'
import { formatInStoreTz } from '@/lib/time'

interface Strain {
  id: string
  name: string
  slug: string
  description?: string
  imageUrl?: string
  strainType?: StrainType
  effects?: string[]
  terpenes?: string[]
  /** Relation counts from the API. Products link through the StrainProduct
   *  join, so the key is `strainProducts`, not `products`. */
  _count?: { strainProducts?: number }
  createdAt?: string
  updatedAt?: string
}

interface PlatformStrain {
  id: string
  name: string
  slug: string
  category: string
  strainType?: string
  imageUrl?: string
  image?: string
  description?: string
  preview?: {
    description?: string
  }
}

type PaginatedData<T> = {
  data: T[]
  meta: {
    page: number
    totalPages: number
    totalItems: number
  }
}

type OptimisticPaginatedContext<T> = {
  previousData: [QueryKey, PaginatedData<T> | undefined][]
}

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(10),
  search: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/catalog/strains')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: searchSchema,
  component: StrainsLayout,
})

let suppressedStrainsAutoOpenSignature: string | null = null

function StrainsLayout() {
  return <Outlet />
}

export function StrainsPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, search: searchQuery } = useSearch({ from: Route.id })
  const shopId = useSelectedShopId()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchQuery}`
  const queryClient = useQueryClient()
  const { resolvedTheme } = useTheme()

  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [isUploading, setIsUploading] = React.useState(false)
  const [strainToDelete, setStrainToDelete] = React.useState<Strain | null>(null)

  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [imageUrl, setImageUrl] = React.useState('')
  const [strainType, setStrainType] = React.useState<StrainType | ''>(StrainType.HYBRID)
  const [effects, setEffects] = React.useState<string[]>([])
  const [terpenes, setTerpenes] = React.useState<string[]>([])
  const [errors, setErrors] = React.useState<{ name?: string }>({})

  // Term whose suggestions the user selected/dismissed — searching resumes as
  // soon as the input no longer matches it (replaces the old skip-one-fetch ref).
  const [suppressedTerm, setSuppressedTerm] = React.useState<string | null>(null)
  const [suggestionsDismissed, setSuggestionsDismissed] = React.useState(false)
  const nameInputRef = React.useRef<HTMLInputElement>(null)
  const suggestionsRef = React.useRef<HTMLDivElement>(null)
  const descriptionRef = React.useRef<HTMLTextAreaElement>(null)
  const [shouldFocusDescription, setShouldFocusDescription] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }), replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/catalog/strains/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }

        navigate({ to: '/catalog/strains', search: (prev) => ({ ...prev }) })
    },
    [navigate]
  )

  const strainsQuery = useQuery({
    queryKey: [...queryKeys.strains(shopId), { page, limit, search: searchQuery }],
    queryFn: async () => {
      const response = await api.get<ListEnvelope>(`/strains`, {
        params: { page, limit, search: searchQuery || undefined, orderBy: 'createdAt', order: 'DESC' },
      })
      const data = extractArray<Strain>(response)
      // The API returns `meta.total`, not `totalItems` — normalize it so the
      // "of N" count and the optimistic add/delete updates read the real total.
      const meta = {
        page: response.meta?.page ?? page,
        totalPages: response.meta?.totalPages ?? 1,
        totalItems: response.meta?.total ?? response.meta?.totalItems ?? data.length,
      }
      return { data, meta }
    },
    enabled: !!shopId,
  })

  const selectedStrainQuery = useQuery({
    queryKey: queryKeys.strain(selectedId),
    queryFn: async () => {
      if (!selectedId) return null
      const response = await api.get<MaybeEnveloped<Strain>>(`/strains/${selectedId}`)
      return response?.data || response
    },
    enabled: !!selectedId,
  })

  const strains = strainsQuery.data?.data || []
  const currentPageHasSelectedStrain = !!selectedId && strains.some((strain) => strain.slug === selectedId || strain.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedStrain) return selectedId
    if (strainsQuery.isLoading || strains.length === 0) return undefined
    if (!selectedId && suppressedStrainsAutoOpenSignature === pageSignature) return undefined
    return strains[0].slug || strains[0].id
  })()
  const selectedStrain = strains.find((s) => s.slug === effectiveSelectedId || s.id === effectiveSelectedId) || selectedStrainQuery.data || null
  const totalItems = strainsQuery.data?.meta?.totalItems || strains.length
  const totalPages = strainsQuery.data?.meta?.totalPages || 1

  const closeSelectedStrain = React.useCallback(() => {
    suppressedStrainsAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/catalog/strains')) {
          suppressedStrainsAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  // Platform strain suggestions for the typed name (debounced via query key).
  const debouncedName = useDebouncedValue(name)
  const platformSearchEnabled =
    !!debouncedName.trim() && !isEditing && debouncedName !== suppressedTerm
  const platformSearchQuery = useQuery({
    queryKey: queryKeys.platformStrainSearch(debouncedName),
    queryFn: async () => {
      const response = await catalogApi.get<{ data?: PlatformStrain[] }>(`/strains`, {
        params: { search: debouncedName, page: 1, limit: 20 },
      })
      return response?.data || []
    },
    enabled: platformSearchEnabled,
    retry: false,
    staleTime: 60_000,
  })
  const platformStrains = React.useMemo(
    () => (platformSearchEnabled ? platformSearchQuery.data ?? [] : []),
    [platformSearchEnabled, platformSearchQuery.data],
  )
  const isSearchingPlatform = platformSearchQuery.isFetching

  // Suggestions show whenever there are results and the user hasn't dismissed
  // them for the current term; a new term resets the dismissal (render-adjust).
  const [prevDebouncedName, setPrevDebouncedName] = React.useState(debouncedName)
  if (prevDebouncedName !== debouncedName) {
    setPrevDebouncedName(debouncedName)
    setSuggestionsDismissed(false)
  }
  const showSuggestions = platformStrains.length > 0 && !suggestionsDismissed

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        nameInputRef.current &&
        !nameInputRef.current.contains(event.target as Node)
      ) {
        setSuggestionsDismissed(true)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const saveStrain = useMutation<void, unknown, Partial<Strain> & { id?: string }, OptimisticPaginatedContext<Strain>>({
    mutationFn: async (payload) => {
      if (payload.id) {
        const { id, ...data } = payload
        await api.patch(`/strains/${id}`, data)
      } else {
        await api.post(`/strains`, payload)
      }
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.strains(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<Strain>>({ queryKey: queryKeys.strains(shopId) })

      queryClient.setQueriesData<PaginatedData<Strain>>(
        { queryKey: queryKeys.strains(shopId) },
        (old) => {
          if (!old) return old

          if (payload.id) {
            return {
              ...old,
              data: old.data.map((item) =>
                item.id === payload.id ? { ...item, ...payload } : item
              ),
            }
          }

          const tempStrain: Strain = {
            id: `temp-${Date.now()}`,
            name: payload.name || '',
            slug: payload.name || '',
            ...payload,
          }

          return {
            ...old,
            data: old.meta.page === 1 ? [tempStrain, ...old.data] : old.data,
            meta: { ...old.meta, totalItems: old.meta.totalItems + 1 },
          }
        }
      )

      return { previousData }
    },
    onSuccess: () => {
      toast.success(`Strain ${isEditing ? 'updated' : 'created'} successfully`)
      handleCloseDialog(false)
    },
    onError: (error: unknown, _payload, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error((error as AxiosError<{ message?: string }>).response?.data?.message || 'Failed to save strain')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.strains(shopId) })
    },
  })

  const deleteStrain = useMutation<void, unknown, string, OptimisticPaginatedContext<Strain>>({
    mutationFn: async (id: string) => {
      await api.delete(`/strains/${id}`)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.strains(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<Strain>>({ queryKey: queryKeys.strains(shopId) })

      queryClient.setQueriesData<PaginatedData<Strain>>(
        { queryKey: queryKeys.strains(shopId) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter((item) => item.id !== id),
            meta: { ...old.meta, totalItems: Math.max(0, old.meta.totalItems - 1) },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_, deletedId) => {
      if (selectedId === deletedId) setSelectedId(null)
      toast.success('Strain deleted successfully')
    },
    onError: (error: unknown, _id, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error('Failed to delete strain', { description: (error as AxiosError<{ message?: string }>).response?.data?.message })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.strains(shopId) })
    },
  })

  const getStrainTypeIcon = (type?: StrainType) => {
    switch (type) {
      case StrainType.SATIVA:
        return <Sun className="h-4 w-4" />
      case StrainType.INDICA:
        return <Moon className="h-4 w-4" />
      case StrainType.HYBRID:
        return <Blend className="h-4 w-4" />
      default:
        return null
    }
  }

  const getStrainTypeColor = (type?: StrainType) => {
    switch (type) {
      case StrainType.SATIVA:
        return 'text-amber-500'
      case StrainType.INDICA:
        return 'text-indigo-500'
      case StrainType.HYBRID:
        return 'text-emerald-500'
      default:
        return 'text-muted-foreground'
    }
  }

  const handleSelectPlatformStrain = (strain: PlatformStrain) => {
    setSuppressedTerm(strain.name)
    setName(strain.name)
    let type = StrainType.HYBRID
    const incomingType = strain.category || strain.strainType
    if (incomingType) {
      const upperType = incomingType.toUpperCase()
      if (upperType === 'SATIVA') type = StrainType.SATIVA
      if (upperType === 'INDICA') type = StrainType.INDICA
      if (upperType === 'HYBRID') type = StrainType.HYBRID
    }
    setStrainType(type)
    if (strain.description || strain.preview?.description) {
      setDescription(strain.description || strain.preview?.description || '')
    }
    if (strain.image || strain.imageUrl) {
      setImageUrl(strain.image || strain.imageUrl || '')
    }
    setSuggestionsDismissed(true)
  }

  const handleCloseDialog = (open: boolean) => {
    setIsAddDialogOpen(open)
    if (!open) {
      setTimeout(() => {
        setName('')
        setDescription('')
        setImageUrl('')
        setStrainType(StrainType.HYBRID)
        setEffects([])
        setTerpenes([])
        setErrors({})
        setIsEditing(false)
        setSuppressedTerm(null)
        setSuggestionsDismissed(false)
        setEditingId(null)
      }, 300)
    }
  }

  const handleEdit = (item: Strain, focusDescription = false) => {
    setEditingId(item.id)
    setName(item.name)
    setDescription(item.description || '')
    setImageUrl(item.imageUrl || '')
    setStrainType(item.strainType || StrainType.HYBRID)
    setEffects(item.effects ?? [])
    setTerpenes(item.terpenes ?? [])
    setIsEditing(true)
    setIsAddDialogOpen(true)
    setShouldFocusDescription(focusDescription)
  }

  const handleSave = () => {
    if (!name.trim()) {
      setErrors({ name: 'Strain name is required' })
      return
    }
    const payload: Partial<Strain> & { id?: string } = { name, description, imageUrl, effects, terpenes }
    if (strainType) payload.strainType = strainType as StrainType
    if (isEditing && editingId) {
      payload.id = editingId
    }
    saveStrain.mutate(payload)
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb entityLabel={selectedStrain?.name} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Strains</h2>
          <p className="text-muted-foreground">Manage cannabis strains and their details.</p>
        </div>
        {(strains.length > 0 || strainsQuery.isLoading) && (
          <Button
            onClick={() => {
              setIsEditing(false)
              setIsAddDialogOpen(true)
            }}
            className="transition-all hover:scale-105 hover:shadow-md"
          >
            <Plus className="mr-0.5 h-4 w-4" /> Add Strain
          </Button>
        )}

        <FormDialog
          open={isAddDialogOpen}
          onOpenChange={handleCloseDialog}
          title={isEditing ? 'Edit Strain' : 'Add Strain'}
          description={
            isEditing
              ? 'Update existing strain details.'
              : "Create a new strain here. Click save when you're done."
          }
          onConfirm={handleSave}
          confirmLabel={
            isUploading
              ? 'Uploading...'
              : isEditing
                ? 'Update Strain'
                : 'Save Strain'
          }
          isPending={saveStrain.isPending}
          isConfirmDisabled={isUploading || !name.trim()}
          size="large"
          onOpenAutoFocus={(e) => {
            if (shouldFocusDescription) {
              e.preventDefault()
              descriptionRef.current?.focus()
            }
          }}
        >
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name" className="gap-0.5">
                  Name<span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="name"
                    ref={nameInputRef}
                    placeholder="e.g. Blue Dream"
                    required
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value)
                      if (errors.name)
                        setErrors((prev) => ({ ...prev, name: undefined }))
                    }}
                    onFocus={() => {
                      if (platformStrains.length > 0 && !isEditing)
                        setSuggestionsDismissed(false)
                    }}
                    className={
                      errors.name
                        ? 'border-destructive focus-visible:ring-destructive'
                        : ''
                    }
                    autoComplete="off"
                  />
                  {isSearchingPlatform && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {showSuggestions && platformStrains.length > 0 && !isEditing && (
                    <div
                      ref={suggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-[200px] overflow-y-auto"
                    >
                      {platformStrains.map((strain, index) => (
                        <div
                          key={`${strain.id}-${index}`}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => handleSelectPlatformStrain(strain)}
                        >
                          <div className="h-8 w-8 rounded-md border flex-shrink-0 overflow-hidden bg-muted/50">
                            {strain.image || strain.imageUrl ? (
                              <ImageWithSkeleton
                                src={(strain.image || strain.imageUrl) as string}
                                alt={strain.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                                <Leaf className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium truncate">
                              {strain.name}
                            </span>
                            <span className="text-xs text-muted-foreground capitalize">
                              {(
                                strain.category ||
                                strain.strainType ||
                                'Hybrid'
                              ).toLowerCase()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label>Strain Type</Label>
                <RadioGroup
                  value={strainType}
                  onValueChange={(value) => setStrainType(value as StrainType)}
                  className="grid grid-cols-3 gap-3"
                >
                  <Label
                    htmlFor="sativa"
                    className={cn(
                      'flex flex-col items-center justify-center gap-2 border-2 rounded-xl p-3 cursor-pointer hover:bg-accent/50 transition-all text-center',
                      strainType === StrainType.SATIVA
                        ? 'border-amber-500 bg-amber-500/5'
                        : 'border-border'
                    )}
                  >
                    <RadioGroupItem
                      value={StrainType.SATIVA}
                      id="sativa"
                      className="sr-only"
                    />
                    <Sun
                      className={cn(
                        'h-6 w-6',
                        strainType === StrainType.SATIVA
                          ? 'text-amber-500'
                          : 'text-muted-foreground'
                      )}
                    />
                    <span
                      className={cn(
                        'font-medium text-xs',
                        strainType === StrainType.SATIVA
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      )}
                    >
                      Sativa
                    </span>
                  </Label>

                  <Label
                    htmlFor="indica"
                    className={cn(
                      'flex flex-col items-center justify-center gap-2 border-2 rounded-xl p-3 cursor-pointer hover:bg-accent/50 transition-all text-center',
                      strainType === StrainType.INDICA
                        ? 'border-indigo-500 bg-indigo-500/5'
                        : 'border-border'
                    )}
                  >
                    <RadioGroupItem
                      value={StrainType.INDICA}
                      id="indica"
                      className="sr-only"
                    />
                    <Moon
                      className={cn(
                        'h-6 w-6',
                        strainType === StrainType.INDICA
                          ? 'text-indigo-500'
                          : 'text-muted-foreground'
                      )}
                    />
                    <span
                      className={cn(
                        'font-medium text-xs',
                        strainType === StrainType.INDICA
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      )}
                    >
                      Indica
                    </span>
                  </Label>

                  <Label
                    htmlFor="hybrid"
                    className={cn(
                      'flex flex-col items-center justify-center gap-2 border-2 rounded-xl p-3 cursor-pointer hover:bg-accent/50 transition-all text-center',
                      strainType === StrainType.HYBRID
                        ? 'border-emerald-500 bg-emerald-500/5'
                        : 'border-border'
                    )}
                  >
                    <RadioGroupItem
                      value={StrainType.HYBRID}
                      id="hybrid"
                      className="sr-only"
                    />
                    <Blend
                      className={cn(
                        'h-6 w-6',
                        strainType === StrainType.HYBRID
                          ? 'text-emerald-500'
                          : 'text-muted-foreground'
                      )}
                    />
                    <span
                      className={cn(
                        'font-medium text-xs',
                        strainType === StrainType.HYBRID
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                      )}
                    >
                      Hybrid
                    </span>
                  </Label>
                </RadioGroup>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  ref={descriptionRef}
                  placeholder="Enter a brief description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="h-[180px] md:h-[220px] resize-none"
                />
              </div>

              <EffectsPicker value={effects} onChange={setEffects} />
              <TerpenesPicker value={terpenes} onChange={setTerpenes} />
            </div>

            <div className="w-full md:w-auto flex flex-col gap-2">
              <Label>Strain Image</Label>
              <div className="flex items-start">
                <ImageUpload
                  value={imageUrl ? [imageUrl] : []}
                  onChange={(url) => setImageUrl(url)}
                  onRemove={() => setImageUrl('')}
                  onUploadStart={() => setIsUploading(true)}
                  onUploadEnd={() => setIsUploading(false)}
                  variant="default"
                />
              </div>
            </div>
          </div>
        </FormDialog>
      </div>

      {(strains.length > 0 || searchQuery || strainsQuery.isLoading) && (
        <div className="flex items-center gap-2 max-w-sm w-full">
          <div className="relative flex-1 group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search strains..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void strainsQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!strainsQuery.isLoading && strains.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'} ${selectedStrain ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full'}`}>
          <div>
            {!strainsQuery.isLoading && strains.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
                <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                  <img src="/misc/logo.png" alt="Rootpoint Logo" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col gap-4 items-center">
                  <p className="font-medium text-muted-foreground text-lg">No strains found</p>
                  <Button
                    onClick={() => {
                      setIsEditing(false)
                      setIsAddDialogOpen(true)
                    }}
                    className="w-fit"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Strain
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Strain</TableHead>                      <TableHead className={`hidden md:table-cell ${selectedStrain ? 'md:hidden xl:table-cell' : ''}`}>Description</TableHead>
                      <TableHead className="w-[110px] text-right">Products</TableHead>
                      <TableHead className="w-[80px] text-right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {strainsQuery.isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Skeleton className="h-10 w-10 rounded-md" />
                              <Skeleton className="h-5 w-32" />
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Skeleton className="h-5 w-48" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-8 ml-auto" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-8 w-8 ml-auto" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      strains.map((item) => (
                        <TableRow
                          key={item.id}
                          className={`cursor-pointer transition-colors group ${effectiveSelectedId === item.id || effectiveSelectedId === item.slug ? 'bg-muted' : ''}`}
                          onClick={() => {
                            if (item.slug === effectiveSelectedId || item.id === effectiveSelectedId) {
                              closeSelectedStrain()
                              return
                            }
                            setSelectedId(item.slug || item.id)
                          }}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-md flex items-center justify-center overflow-hidden bg-muted/50">
                                <ImageWithSkeleton
                                  src={item.imageUrl || (resolvedTheme === 'dark' ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png' : '/placeholder-light/Place%20Holder%20Light%204_4.png')}
                                  alt={item.name}
                                  wrapperClassName="h-full w-full"
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  {item.name}
                                  {item.strainType && (
                                    <span className={getStrainTypeColor(item.strainType)}>
                                      {getStrainTypeIcon(item.strainType)}
                                    </span>
                                  )}
                                </div>
                                {item.slug && <span className="block whitespace-normal break-all text-xs text-muted-foreground">{item.slug}</span>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className={`hidden md:table-cell text-muted-foreground ${selectedStrain ? 'md:hidden xl:table-cell' : ''}`}>
                            {item.description ? (
                              <div className="text-sm">
                                <TooltipProvider delayDuration={1500}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help">
                                        {item.description.split(' ').length > 7
                                          ? item.description.split(' ').slice(0, 7).join(' ') + ' ...'
                                          : item.description}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs break-words">{item.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer min-w-[110px]"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEdit(item, true)
                                }}
                              >
                                <span className="text-muted-foreground group-hover:hidden whitespace-nowrap">—</span>
                                <span className="text-muted-foreground hidden group-hover:inline text-sm hover:text-foreground transition-colors whitespace-nowrap">
                                  Add description
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {item._count?.strainProducts ?? 0}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                                  <span className="sr-only">Open menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-32">
                                <DropdownMenuItem className="gap-1.5" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
                                  <Pencil className="h-4 w-4" />Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem className="gap-1.5 text-destructive focus:text-destructive" onClick={(e) => { e.stopPropagation(); setStrainToDelete(item); }}>
                                  <Trash2 className="h-4 w-4" />Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  entityName="strains"
                  isLoading={strainsQuery.isLoading}
                  onPageChange={(p: number) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
                  onLimitChange={(l: number) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
                />
              </>
            )}
          </div>
        </div>

        {selectedStrain && (
          <Card className="squircle mx-0.5 mb-0.5 w-full md:w-[320px] lg:w-[360px] sticky top-0 flex flex-col h-fit max-h-[calc(100vh-6.5rem)] overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 shadow-lg pb-4">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-card z-10">
              <div className="grid gap-0">
                <CardTitle className="text-xl flex items-center gap-1.5 leading-tight">{selectedStrain.name}</CardTitle>
                <CardDescription className="font-mono text-xs -mt-0.5">{selectedStrain.slug}</CardDescription>
              </div>
              <div className="flex items-center gap-1 -mt-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground"
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem
                      onClick={() => handleEdit(selectedStrain)}
                      className="gap-1.5 cursor-pointer"
                    >
                      <Pencil className="h-4 w-4" />Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setStrainToDelete(selectedStrain)}
                      className="gap-1.5 text-red-500 hover:text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeSelectedStrain}
                  className="h-9 w-9 transition-all hover:bg-muted hover:scale-105 duration-200"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 overflow-y-auto pb-0 px-6">
              <div className="flex flex-row gap-4 items-start">
                 <div className="w-24 h-24 rounded-lg bg-muted/50 flex items-center justify-center shrink-0 overflow-hidden relative">
                  <ImageWithSkeleton
                    src={selectedStrain.imageUrl || (resolvedTheme === 'dark' ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png' : '/placeholder-light/Place%20Holder%20Light%204_4.png')}
                    alt={selectedStrain.name}
                    className="w-full h-full object-cover"
                  />
                 </div>
                <div className="space-y-1 flex-1">
                  {selectedStrain.strainType && (
                    <div className="mb-4">
                      <h4 className="font-medium leading-none mb-1.5">Type</h4>
                      <div className={`flex items-center gap-1.5 text-sm ${getStrainTypeColor(selectedStrain.strainType)}`}>
                        {getStrainTypeIcon(selectedStrain.strainType)}
                        <span className="capitalize">{selectedStrain.strainType.toLowerCase()}</span>
                      </div>
                    </div>
                  )}
                  <h4 className="font-medium leading-none">Description</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {selectedStrain.description || (
                      <span
                        className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors inline-block scale-x-150"
                        title="Add description"
                        onClick={() => handleEdit(selectedStrain, true)}
                      >
                        —
                      </span>
                    )}
                  </p>
                </div>
              </div>
              {(selectedStrain.effects?.length || selectedStrain.terpenes?.length) ? (
                <div className="space-y-3">
                  {selectedStrain.effects && selectedStrain.effects.length > 0 && (
                  <div>
                    <h4 className="font-medium leading-none mb-2">Effects</h4>
                    <div className="flex flex-wrap items-center gap-2">
                      {selectedStrain.effects.map((e) => (
                        <EffectChip key={e} value={e} />
                      ))}
                    </div>
                  </div>
                  )}
                  {selectedStrain.terpenes && selectedStrain.terpenes.length > 0 && (
                  <div>
                    <h4 className="font-medium leading-none mb-2">Dominant terpenes</h4>
                    <div className="flex flex-wrap items-center gap-2">
                      {selectedStrain.terpenes.map((t) => (
                        <span
                          key={t}
                          className="inline-flex h-7 select-none items-center rounded-full border bg-muted/50 px-2.5 text-xs text-muted-foreground"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
              ) : null}
              <div className="pt-2 mt-2 border-t border-border/50 text-xs text-muted-foreground">
                <span className="font-medium text-foreground block mb-1">Products</span>
                <span className="tabular-nums">{selectedStrain._count?.strainProducts ?? 0}</span>
              </div>

              <div className="pt-2 mt-2 border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground block mb-1">Created</span>
                  {selectedStrain.createdAt ? formatInStoreTz(selectedStrain.createdAt, 'MMM d, yyyy') : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-foreground block mb-1">Updated</span>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {selectedStrain.updatedAt ? formatDistanceToNow(new Date(selectedStrain.updatedAt), { addSuffix: true }) : 'N/A'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <DeleteConfirmationDialog
        open={!!strainToDelete}
        onOpenChange={(open: boolean) => !open && setStrainToDelete(null)}
        title="Delete Strain?"
        description={
          <>
            This action cannot be undone. This will permanently delete the strain{' '}
            <span className="font-medium text-foreground">"{strainToDelete?.name}"</span> and
            remove it from our servers.
          </>
        }
        onConfirm={() => {
          if (strainToDelete) {
            deleteStrain.mutate(strainToDelete.id)
            setStrainToDelete(null)
          }
        }}
        confirmLabel="Delete Strain"
        isPending={deleteStrain.isPending}
      />
    </div>
  )
}
